import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format as formatearConPatron, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Convierte a Date una fecha guardada como 'YYYY-MM-DD'.
 *
 * `new Date('2026-07-27')` se interpreta como medianoche UTC, así que en países
 * con horario por detrás de Greenwich (América) se mostraría el día ANTERIOR.
 * Añadiendo la hora local se lee siempre el día correcto.
 */
export function parseLocalDate(value) {
  if (!value) return null;
  const s = String(value);
  const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T00:00:00`) : new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

export function formatDate(date, formatStr = 'dd/MM/yyyy') {
  if (!date) return '';
  try {
    const d = parseLocalDate(date);
    return d ? formatearConPatron(d, formatStr, { locale: es }) : String(date);
  } catch {
    return date;
  }
}

/**
 * Formatea una fecha con un patrón de date-fns, en español y SIN romperse.
 *
 * Por qué existe: por toda la aplicación se hacía
 * `format(new Date(fecha + 'T00:00:00'), '...')`. Si la fecha venía con hora
 * (p. ej. '2026-05-23T00:00:00', como la devuelve la API en algunos casos),
 * el resultado era '2026-05-23T00:00:00T00:00:00' → fecha inválida → date-fns
 * lanza una excepción → la pantalla se queda EN BLANCO. Aquí se tolera
 * cualquiera de los dos formatos y, si no hay fecha válida, se devuelve ''.
 */
export function formatearFecha(value, pattern = 'dd/MM/yyyy') {
  const d = parseLocalDate(value);
  if (!d) return '';
  try {
    return formatearConPatron(d, pattern, { locale: es });
  } catch {
    return '';
  }
}

/**
 * Igual que `formatearFecha` pero con las opciones nativas de
 * `toLocaleDateString('es-ES', ...)`.
 */
export function fechaLocal(value, opciones = {}) {
  const d = parseLocalDate(value);
  if (!d) return '';
  try {
    return d.toLocaleDateString('es-ES', opciones);
  } catch {
    return '';
  }
}

export function truncate(str, n) {
  return str?.length > n ? str.substr(0, n - 1) + '...' : str;
}

/**
 * Normaliza un texto para buscar: minúsculas y SIN tildes ni diéresis.
 *
 * Por qué existe: los buscadores comparaban con `toLowerCase().includes(...)`,
 * así que escribir "fabian" no encontraba a "Fabián Andrei" ni "seleccion" a
 * "Selección". Un entrenador escribiendo con el móvil casi nunca pone tildes.
 * La ñ se conserva (buscar "nino" no debe encontrar "niño").
 */
export function normalizarTexto(value) {
  if (value == null) return '';
  return String(value)
    .normalize('NFD')
    // Quita los acentos combinantes (U+0300–U+036F) EXCEPTO la virgulilla
    // U+0303, para que la ñ siga siendo una ñ.
    .replace(/[\u0300-\u0302\u0304-\u036f]/g, '')
    // Se vuelve a componer para que la ñ sea un solo carácter y el resultado
    // se pueda comparar con un texto normal.
    .normalize('NFC')
    .toLowerCase();
}

/**
 * ¿Alguno de los campos contiene lo buscado? Ignora tildes y mayúsculas.
 * Con la búsqueda vacía devuelve true (no filtra nada).
 */
export function coincideBusqueda(query, ...campos) {
  const q = normalizarTexto(query).trim();
  if (!q) return true;
  return campos.some(c => normalizarTexto(c).includes(q));
}

/**
 * "hace 3 días" a partir de una fecha, tolerando fechas inválidas.
 *
 * `formatDistanceToNow(new Date(x))` LANZA una excepción si `x` falta o no es
 * una fecha, y eso tumbaba el panel de notificaciones entero.
 */
export function haceCuanto(value) {
  const d = parseLocalDate(value);
  if (!d) return '';
  try {
    return formatDistanceToNow(d, { addSuffix: true, locale: es });
  } catch {
    return '';
  }
}

/**
 * Versión translúcida de un color, sirva o no una variable CSS.
 *
 * Por qué existe: por toda la aplicación se hacía `${color}15` para conseguir un
 * fondo suave. Eso solo funciona si `color` es un hexadecimal; en cuanto vale
 * `var(--primary)` el resultado es `var(--primary)15`, que NO es CSS válido, así
 * que el fondo simplemente no se pintaba. Con `color-mix` funciona en los dos
 * casos y además permite pasar los colores del tema.
 */
export function conAlfa(color, porcentaje = 12) {
  if (!color) return 'transparent';
  return `color-mix(in srgb, ${color} ${porcentaje}%, transparent)`;
}

/**
 * Nombre para la copia de algo: "Sesión" → "Sesión (copia)".
 *
 * Duplicar dos veces daba "Sesión (copia) (copia)"; a partir de la segunda se
 * numera: "Sesión (copia 2)", "Sesión (copia 3)"...
 */
export function nombreDeCopia(nombre, porDefecto = 'Sesión') {
  const base = (nombre || '').trim() || porDefecto;
  const m = base.match(/^(.*) \(copia(?: (\d+))?\)$/);
  if (!m) return `${base} (copia)`;
  return `${m[1]} (copia ${m[2] ? Number(m[2]) + 1 : 2})`;
}
